import type { ChatbotState } from "./bot.types";
import { create } from "zustand";
import type { BotStore } from "./bot.types";

/**
 * The one line of a reasoning stream worth showing as a status detail: its last non-blank line,
 * capped. Exported because `chat-runtime.ts` buffers the description of a stream whose conversation
 * is closed and must reduce it exactly as this store would — one rule, one implementation.
 */
export const excerptReasoning = (reasoning: string): string => {
  const lines = reasoning
    .trim()
    .split("\n")
    .filter((line) => line.trim());
  const lastLine = lines[lines.length - 1] || reasoning;

  return lastLine.length > 100 ? `${lastLine.slice(0, 100)}...` : lastLine;
};

export const useBotStore = create<BotStore>((set, get) => ({
  botName: "",
  version: "",
  chatbotState: null,
  actionDescription: "",
  messageActionDescriptions: new Map<string, string>(),

  setBotName: (botName: string) => set({ botName }),
  setVersion: (version: string) => set({ version }),
  setChatbotState: (chatbotState: ChatbotState) => set({ chatbotState }),

  startActionDescription: () => set({ actionDescription: "" }),
  appendActionDescription: (chunk: string) => set((state) => ({ actionDescription: state.actionDescription + chunk })),
  endActionDescription: () => {},
  clearActionDescription: () => set({ actionDescription: "" }),
  setReasoningAsActionDescription: (reasoning: string) => {
    // Only set if no action description yet (don't override tool's user_explanation)
    if (get().actionDescription) return;

    set({ actionDescription: excerptReasoning(reasoning) });
  },

  saveActionDescriptionForMessage: (messageId: string) => {
    const currentDesc = get().actionDescription;
    if (currentDesc && messageId) {
      set((state) => {
        const newMap = new Map(state.messageActionDescriptions);
        newMap.set(messageId, currentDesc);
        return { messageActionDescriptions: newMap, actionDescription: "" };
      });
    }
  },

  getActionDescriptionForMessage: (messageId: string) => {
    return get().messageActionDescriptions.get(messageId) ?? null;
  },

  clearMessageActionDescriptions: () => set({ messageActionDescriptions: new Map() }),

  getBotNameAndVersion: () => {
    const botName = get().botName;
    if (!botName) return null;
    const version = get().version;
    return `${botName} (${version})`;
  },
}));

export const useBotName = () => useBotStore((state) => state.botName);
export const useVersion = () => useBotStore((state) => state.version);
export const useChatbotState = () => useBotStore((state) => state.chatbotState);
export const useActionDescription = () => useBotStore((state) => state.actionDescription);
export const useMessageActionDescription = (messageId: string) =>
  useBotStore((state) => state.messageActionDescriptions.get(messageId) ?? null);

export const useSetBotName = () => useBotStore((state) => state.setBotName);
export const useSetVersion = () => useBotStore((state) => state.setVersion);
export const useSetChatbotState = () => useBotStore((state) => state.setChatbotState);

export const useBotNameAndVersion = () => useBotStore((state) => state.getBotNameAndVersion());
